/**
 * JSON-LD schema builders for SEO rich snippets.
 * Each function returns a plain object passed to <Seo jsonLd={...} />
 * (or as an item of a jsonLd array).
 */

const SITE = "https://luigichauveau.fr";
const DEFAULT_LOGO = `${SITE}/brand/logo-512.png`;

/**
 * Schema.org LocalBusiness — fed by /api/site/business response.
 * Returns null if business minimum data is missing.
 */
export function buildLocalBusiness(business) {
  if (!business) return null;
  const addr = {};
  if (business.address) addr.streetAddress = business.address;
  if (business.postal_code) addr.postalCode = business.postal_code;
  if (business.city) addr.addressLocality = business.city;
  if (business.region) addr.addressRegion = business.region;
  addr.addressCountry = business.country || "FR";

  const sameAs = [];
  if (business.instagram) sameAs.push(`https://www.instagram.com/${business.instagram.replace(/^@/, "")}/`);

  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "GeneralContractor"],
    "@id": `${SITE}/#business`,
    name: business.name || "Luigi Chauveau",
    legalName: business.legal_name || undefined,
    url: SITE,
    logo: business.logo || DEFAULT_LOGO,
    image: business.logo || DEFAULT_LOGO,
    telephone: business.phone || "+33761051061",
    email: business.email || "contact@luigichauveau.fr",
    address: { "@type": "PostalAddress", ...addr },
    areaServed: [
      { "@type": "AdministrativeArea", name: "Loire-Atlantique" },
      { "@type": "City", name: "Nantes" },
      { "@type": "City", name: "Bouguenais" },
      { "@type": "City", name: "La Baule" },
      { "@type": "City", name: "Pornichet" },
      { "@type": "City", name: "Le Pouliguen" },
      { "@type": "City", name: "Pornic" },
      { "@type": "City", name: "Saint-Brevin" },
    ],
    knowsAbout: [
      "Menuiserie agencement",
      "Cuisine sur-mesure",
      "Dressing sur-mesure",
      "Bibliothèque sur-mesure",
      "Remise en état après sinistre",
      "Coordination assurance",
      "Travail du chêne, noyer, frêne, hêtre",
    ],
    priceRange: "€€€",
    identifier: business.siret || undefined,
    sameAs: sameAs.length ? sameAs : undefined,
    foundingDate: "2015",
    description:
      "Luigi Chauveau, menuisier agenceur en Loire-Atlantique. Agencements sur-mesure en matières franches. Formé chez les Compagnons du Devoir, artisan depuis 2015.",
  };
}

/**
 * Schema.org Person — for "Le savoir-faire" page.
 */
export function buildPerson() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE}/#luigi`,
    name: "Luigi Chauveau",
    jobTitle: "Menuisier agenceur",
    worksFor: { "@id": `${SITE}/#business` },
    url: SITE,
    image: DEFAULT_LOGO,
    alumniOf: { "@type": "Organization", name: "Compagnons du Devoir" },
    knowsAbout: [
      "Menuiserie",
      "Agencement sur-mesure",
      "Travail du chêne, noyer, frêne, hêtre",
      "Coordination de chantier après sinistre",
    ],
    description: "Artisan menuisier agenceur formé chez les Compagnons du Devoir, à son compte depuis 2015 en Loire-Atlantique.",
  };
}

/**
 * Schema.org BreadcrumbList.
 * items: array of { name, path } in display order (Home auto-prepended).
 */
export function buildBreadcrumbs(items = []) {
  const list = [{ name: "Accueil", path: "/" }, ...items];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: list.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

/**
 * Schema.org CreativeWork — Project (realisation).
 */
export function buildProject(realisation) {
  if (!realisation) return null;
  const cat =
    realisation.categorie === "sinistre"
      ? "Sinistre"
      : realisation.categorie === "particulier"
        ? "Particulier"
        : realisation.categorie === "professionnel"
          ? "Professionnel"
          : "Projet";
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": `${SITE}/realisations/${realisation.slug}`,
    name: realisation.titre,
    headline: realisation.titre,
    description: realisation.chapeau || realisation.sous_titre || `Réalisation ${cat} par Luigi Chauveau.`,
    image: realisation.photo_couverture,
    dateCreated: realisation.annee ? `${realisation.annee}-01-01` : undefined,
    creator: { "@id": `${SITE}/#luigi` },
    publisher: { "@id": `${SITE}/#business` },
    keywords: [cat, ...(realisation.materiaux || [])].filter(Boolean).join(", "),
    locationCreated: realisation.lieu
      ? { "@type": "Place", name: realisation.lieu }
      : undefined,
    url: `${SITE}/realisations/${realisation.slug}`,
  };
}

/**
 * Schema.org FAQPage — from array of {q, a}.
 */
export function buildFaqPage(faqs = []) {
  if (!faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/**
 * Schema.org Service — for local SEO city pages.
 */
export function buildLocalService({ city, slug }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Menuiserie agencement sur-mesure",
    provider: { "@id": `${SITE}/#business` },
    areaServed: { "@type": "City", name: city, containedInPlace: { "@type": "AdministrativeArea", name: "Loire-Atlantique" } },
    url: `${SITE}/menuisier-${slug}`,
    name: `Menuisier agenceur à ${city}`,
    description: `Luigi Chauveau, menuisier agenceur intervient à ${city} et alentours. Cuisines, dressings, bibliothèques, remises en état après sinistre.`,
  };
}
